import { EditOutlined } from '@mui/icons-material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { FormHelperText, IconButton, TextField } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { editMicroLearning } from '../../../../api/api'
import { MicroLearningSettings } from '../../../../api/models'
import { AppStateContext } from '../../../../state/AppProvider'
import sharedStyles from '../../MicroLearningDetail.module.css'
import { MicroLearningActionModal } from '../MicroLearningActionModal'
import styles from './ManualEditModal.module.css'

const inputTextSx = {
  fontFamily: 'Helvetica, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '23px',
  letterSpacing: '0.15px',
}

const DEFAULT_CONSTRAINTS: MicroLearningSettings = {
  enabled: false,
  title_min_length: 5,
  title_max_length: 200,
  summary_min_length: 5,
  summary_max_length: 500,
  recommended_actions_min: 2,
  recommended_actions_max: 5,
  recommended_action_min_length: 5,
  history_page_size: 20,
}

export type ManualEditData = {
  ml_id: string
  title: string
  summary: string
  recommended_actions: string[]
  status: string
}

export type ManualEditResult = {
  ml_id: string
  title: string
  summary: string
  recommended_actions: string[]
  version: number
  new_status: string
}

type ManualEditModalProps = {
  open: boolean
  onClose: () => void
  onSuccess: (result: ManualEditResult) => void
  microLearning: ManualEditData
}

export default function ManualEditModal({ open, onClose, onSuccess, microLearning }: ManualEditModalProps) {
  const appStateContext = useContext(AppStateContext)
  const constraints = appStateContext?.state.frontendSettings?.micro_learning ?? DEFAULT_CONSTRAINTS

  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [recommendedActions, setRecommendedActions] = useState<string[]>(['', ''])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) {
      setTitle(microLearning.title)
      setSummary(microLearning.summary)
      setRecommendedActions(
        microLearning.recommended_actions.length >= constraints.recommended_actions_min
          ? [...microLearning.recommended_actions]
          : [
              ...microLearning.recommended_actions,
              ...Array(constraints.recommended_actions_min - microLearning.recommended_actions.length).fill(''),
            ],
      )
      setTouchedFields(new Set())
      setIsSubmitting(false)
    }
  }, [
    open,
    microLearning.ml_id,
    microLearning.title,
    microLearning.summary,
    microLearning.recommended_actions,
    constraints.recommended_actions_min,
  ])

  const markTouched = (field: string) => {
    setTouchedFields(prev => new Set(prev).add(field))
  }

  const titleError = useMemo(() => {
    if (!touchedFields.has('title')) return ''
    if (!title.trim()) return 'Title is required'
    if (title.trim().length < constraints.title_min_length)
      return `Title must be at least ${constraints.title_min_length} characters`
    if (title.length > constraints.title_max_length)
      return `Title must be at most ${constraints.title_max_length} characters`
    return ''
  }, [title, touchedFields, constraints])

  const summaryError = useMemo(() => {
    if (!touchedFields.has('summary')) return ''
    if (!summary.trim()) return 'Summary is required'
    if (summary.trim().length < constraints.summary_min_length)
      return `Summary must be at least ${constraints.summary_min_length} characters`
    if (summary.length > constraints.summary_max_length)
      return `Summary must be at most ${constraints.summary_max_length} characters`
    return ''
  }, [summary, touchedFields, constraints])

  const getActionError = (index: number) => {
    if (!touchedFields.has(`action-${index}`)) return ''
    const trimmed = recommendedActions[index].trim()
    if (!trimmed) return 'Recommended Action cannot be empty'
    if (trimmed.length < constraints.recommended_action_min_length)
      return `Recommended Action must be at least ${constraints.recommended_action_min_length} characters`
    return ''
  }

  const isFormValid = useMemo(() => {
    const titleLen = title.trim().length
    const isTitleValid = titleLen >= constraints.title_min_length && title.length <= constraints.title_max_length
    const summaryLen = summary.trim().length
    const isSummaryValid =
      summaryLen >= constraints.summary_min_length && summary.length <= constraints.summary_max_length
    const actionsCount = recommendedActions.length
    const areActionsValid =
      actionsCount >= constraints.recommended_actions_min &&
      actionsCount <= constraints.recommended_actions_max &&
      recommendedActions.every(a => a.trim().length >= constraints.recommended_action_min_length)
    return isTitleValid && isSummaryValid && areActionsValid
  }, [title, summary, recommendedActions, constraints])

  const handleAddAction = () => {
    if (recommendedActions.length < constraints.recommended_actions_max) {
      setRecommendedActions(prev => [...prev, ''])
    }
  }

  const handleRemoveAction = (index: number) => {
    setRecommendedActions(prev => prev.filter((_, i) => i !== index))
    setTouchedFields(prev => {
      const next = new Set(prev)
      next.delete(`action-${index}`)
      return next
    })
  }

  const handleActionChange = (index: number, value: string) => {
    setRecommendedActions(prev => prev.map((a, i) => (i === index ? value : a)))
  }

  const submitEdit = async () => {
    setIsSubmitting(true)

    const result = await editMicroLearning(microLearning.ml_id, {
      title: title.trim(),
      summary: summary.trim(),
      recommended_actions: recommendedActions.map(a => a.trim()),
    })

    setIsSubmitting(false)

    if (result) {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Micro-Learning edited successfully.', severity: 'success' },
      })
      onSuccess({
        ml_id: microLearning.ml_id,
        title: title.trim(),
        summary: summary.trim(),
        recommended_actions: recommendedActions.map(a => a.trim()),
        version: result.version,
        new_status: result.new_status,
      })
      onClose()
    } else {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Failed saving Micro-Learning changes. Try again later.', severity: 'warning' },
      })
    }
  }

  return (
    <MicroLearningActionModal
      open={open}
      onClose={onClose}
      onConfirm={submitEdit}
      confirmText="Save Changes"
      confirmDisabled={!isFormValid || isSubmitting}
      theme="gray"
      headerIcon={<EditOutlined style={{ color: '#616161', fontSize: 18 }} />}
      title="Manually Edit"
      subtitle="Update the details manually"
      showMlCard={false}
      maxWidth="md">
      <div className={styles.formContent}>
        <p className={styles.mlId}>{microLearning.ml_id}</p>

        <div className={styles.summaryGroup}>
          <div className={`${styles.fieldBox} ${titleError ? styles.fieldBoxError : ''}`}>
            <span className={styles.fieldLabel}>Title</span>
            <TextField
              value={title}
              onChange={e => {
                if (e.target.value.length <= constraints.title_max_length || e.target.value.length < title.length) {
                  setTitle(e.target.value)
                }
              }}
              onBlur={() => markTouched('title')}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': { padding: 0 },
                '& .MuiOutlinedInput-input': { padding: 0, ...inputTextSx },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            />
          </div>
          {titleError && (
            <FormHelperText
              error
              sx={{ ml: '14px' }}>
              {titleError}
            </FormHelperText>
          )}
          <div className={sharedStyles.charCount}>
            <span>
              {title.length}/{constraints.title_max_length} characters
            </span>
          </div>
        </div>

        <div className={styles.summaryGroup}>
          <div>
            <div className={`${styles.fieldBox} ${summaryError ? styles.fieldBoxError : ''}`}>
              <span className={styles.fieldLabel}>Summary</span>
              <TextField
                value={summary}
                onChange={e => {
                  if (
                    e.target.value.length <= constraints.summary_max_length ||
                    e.target.value.length < summary.length
                  ) {
                    setSummary(e.target.value)
                  }
                }}
                onBlur={() => markTouched('summary')}
                fullWidth
                multiline
                minRows={4}
                sx={{
                  '& .MuiOutlinedInput-root': { padding: 0, ...inputTextSx },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
              />
            </div>
            {summaryError && (
              <FormHelperText
                error
                sx={{ ml: '14px' }}>
                {summaryError}
              </FormHelperText>
            )}
          </div>

          <div className={sharedStyles.charCount}>
            <span>
              {summary.length}/{constraints.summary_max_length} characters
            </span>
          </div>
        </div>

        <div className={styles.actionsSection}>
          <p className={styles.actionsSectionTitle}>Recommended Actions</p>
          <p className={styles.actionsSectionHelper}>You need to have at least two Recommended Actions to proceed</p>

          {recommendedActions.map((action, index) => (
            <div
              key={index}
              className={styles.actionRow}>
              <TextField
                className={styles.actionField}
                value={action}
                onChange={e => handleActionChange(index, e.target.value)}
                onBlur={() => markTouched(`action-${index}`)}
                error={!!getActionError(index)}
                helperText={getActionError(index) || undefined}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '45px',
                    padding: '0 9px',
                  },
                  '& .MuiOutlinedInput-input': { padding: '0', ...inputTextSx },
                }}
                placeholder="Insert a Recommended Action"
              />
              <IconButton
                className={styles.deleteButton}
                onClick={() => handleRemoveAction(index)}>
                <DeleteOutlineIcon />
              </IconButton>
            </div>
          ))}
        </div>

        <button
          className={styles.addAction}
          onClick={handleAddAction}
          disabled={recommendedActions.length >= constraints.recommended_actions_max}
          type="button">
          <AddCircleOutlineIcon className={styles.addActionIcon} />
          Add Recommended Action
        </button>
      </div>
    </MicroLearningActionModal>
  )
}
